import { useId } from 'react';
import { Checkbox } from '../../../components/forms';
import type { ReceivingDuplicate } from '../../../data/receiving';
import { formatShortDate } from '../../../helpers/format-date';
import { pluralize } from '../../../helpers/pluralize';
import {
  FIGURES,
  FilePanel,
  ReviewBandSection,
  ROSE_FIGURES,
  ROSE_WORDS,
  WORDS,
} from './invoice-totals-summary';

export type DuplicateInvoicesProps = {
  duplicates: ReceivingDuplicate[];
  // Whether the owner has ticked "receive it anyway". Held by ReviewResults,
  // which gates Send on it and passes it through to Confirm.
  acknowledged: boolean;
  onAcknowledgedChange: (acknowledged: boolean) => void;
  readOnly: boolean;
};

// The two ways a duplicate is recognised read differently to an owner: one is
// a fact about the file, the other about what is on it.
const matchLabel = (kind: ReceivingDuplicate['kind']) =>
  kind === 'identical_bytes' ? 'Same file as' : 'Same invoice as';

// Drawn in the invoice-totals vocabulary, because it is the other
// reconciliation the owner reads on this screen: the file it is about heads
// the panel, WORDS name what was matched, FIGURES say what, and the one rose
// row is the finding — the day this invoice already went through.
function DuplicatePanel({ duplicate }: { duplicate: ReceivingDuplicate }) {
  return (
    <FilePanel filename={duplicate.filename}>
      <table className="border-collapse">
        <tbody>
          <tr>
            <th scope="row" className={`py-1 pr-4 text-left whitespace-nowrap ${WORDS}`}>
              {matchLabel(duplicate.kind)}
            </th>
            <td className={`py-1 pl-4 ${FIGURES}`}>{duplicate.matched_filename}</td>
          </tr>
          <tr>
            <th scope="row" className={`py-1 pr-4 text-left whitespace-nowrap ${ROSE_WORDS}`}>
              Received
            </th>
            <td className={`py-1 pl-4 ${ROSE_FIGURES}`}>
              {formatShortDate(new Date(duplicate.received_at))}
            </td>
          </tr>
        </tbody>
      </table>
    </FilePanel>
  );
}

// ORD-451. Best-effort guard: Send stays disabled until the owner ticks the
// box, because past Confirm the file is on its way to LiquorPOS and the
// receiving can't be cancelled. The invoice-totals check can't catch this: two
// copies of one invoice each agree with their own printed total, so the Review
// screen is at its most reassuring exactly when the quantities have doubled.
export function DuplicateInvoices({
  duplicates,
  acknowledged,
  onAcknowledgedChange,
  readOnly,
}: DuplicateInvoicesProps) {
  const checkboxId = useId();

  if (duplicates.length === 0) {
    return null;
  }

  return (
    <ReviewBandSection
      testId="duplicate-invoices"
      eyebrow="Already received"
      footer={
        !readOnly && (
          <div className="flex items-center gap-2">
            <Checkbox
              id={checkboxId}
              checked={acknowledged}
              onChange={(e) => onAcknowledgedChange(e.target.checked)}
            />
            <label htmlFor={checkboxId} className={`cursor-pointer ${WORDS}`}>
              Receive {pluralize(duplicates.length, 'it', 'them')} anyway
            </label>
          </div>
        )
      }
    >
      {duplicates.map((duplicate) => (
        <DuplicatePanel key={duplicate.filename} duplicate={duplicate} />
      ))}
    </ReviewBandSection>
  );
}
